import { Alert, Col, Divider, Form, Row, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"
import { useTranslation } from "react-i18next"

import { CustomerAutoComplete } from "../../../../components/AutoComplete/CustomerAutoComplete.tsx"
import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput.tsx"
import {
  CUSTOMER_FIRST_NAME_LABEL,
  CUSTOMER_FIRST_NAME_PLACEHOLDER
} from "../../../../constants/customer-name.ts"
import { placeholders } from "../../../../constants/placeholders"
import { normalizeEmail } from "../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../utils/validation/validate-phone.ts"
import { type CustomerSearchResult } from "../../../CustomersPage/types/customer.types.ts"
import type { ProjectWizardFormData } from "./project-wizard-data.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

interface ProjectWizardCustomerStepProps {
  form: FormInstance<ProjectWizardFormData>
}

export function ProjectWizardCustomerStep({
  form
}: ProjectWizardCustomerStepProps) {
  const { t } = useTranslation()

  const onCustomerSelect = (selected: CustomerSearchResult) => {
    form.setFieldsValue({
      customer: {
        first_name: selected?.first_name || null,
        last_name: selected?.last_name || null,
        phone: selected?.phone || null,
        email: selected?.email || null
      }
    })
  }

  const onCustomerClear = () => {
    form.setFieldValue("customer", null)
  }

  return (
    <>
      <Typography.Text type={"secondary"} className={"mb-1 block text-xs"}>
        {t("projectWizard.customer.searchLabel")}
      </Typography.Text>
      <CustomerAutoComplete
        onSelect={onCustomerSelect}
        onClear={onCustomerClear}
      />

      <Divider plain>{t("projectWizard.customer.or")}</Divider>

      <Typography.Title level={5}>
        {t("projectWizard.customer.sectionTitle")}
      </Typography.Title>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={CUSTOMER_FIRST_NAME_LABEL}
            name={["customer", "first_name"]}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("projectWizard.customer.firstNameRequired")
              }
            ]}
          >
            <TextInput placeholder={CUSTOMER_FIRST_NAME_PLACEHOLDER} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.customer.lastName")}
            name={["customer", "last_name"]}
          >
            <TextInput
              placeholder={t("projectWizard.customer.lastNamePlaceholder")}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.customer.phoneOptional")}
            name={["customer", "phone"]}
            rules={[{ validator: validatePhone }]}
          >
            <PhoneInput />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.customer.emailOptional")}
            name={["customer", "email"]}
            rules={[
              {
                type: "email",
                message: t("projectWizard.customer.emailInvalid")
              }
            ]}
            normalize={(value: string) => normalizeEmail(value) || null}
          >
            <TextInput disableAutoCapitalize placeholder={placeholders.email} />
          </Form.Item>
        </Col>
      </Row>

      <Alert
        type={"info"}
        showIcon
        message={t("projectWizard.customer.minimumHint")}
        description={t("projectWizard.customer.minimumHintDescription")}
      />
    </>
  )
}
